import { useApi } from '@backstage/core-plugin-api';
import { configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { useToast } from '@WESCO-International/wdp-ui-components/components/toast';
import useAsync from 'react-use/lib/useAsync';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  buildSubComponentColumns,
  buildLinks,
  buildApiSubRows,
} from '../common/utils';
import { getAllEntities } from '../common/getAllEntities';

type DropdownOption = { label: string; value: string };

// Hook responsible for fetching and deriving all catalog details data.
export const useCatalogDetails = ({
  item,
  displayName,
  stateKind,
  stateType,
  isAdmin,
}: {
  item: any;
  displayName: string;
  stateKind: string;
  stateType: string;
  isAdmin: boolean;
}) => {
  const toast = useToast();
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const readyLifecycleOptions: DropdownOption[] = useMemo(
    () => [{ label: 'Ready to Use', value: 'Ready to Use' }],
    [],
  );

  const {
    value: allComponents,
    loading: componentsLoading,
    error: componentsError,
  } = useAsync(async () => {
    return await getAllEntities(catalogApi, {
      filter: [{ kind: 'Component' }],
    });
  }, [catalogApi]);

  const { value: apiList } = useAsync(async () => {
    return await getAllEntities(catalogApi, { filter: [{ kind: 'API' }] });
  }, [catalogApi]);

  const isApi = useMemo(() => {
    if (stateKind) return stateKind === 'api';
    if (stateType === 'api') return true;
    const foundApi = (apiList ?? []).some(
      (e: any) => (e?.metadata?.name ?? '') === displayName,
    );
    if (foundApi) return true;
    const foundComponent = (allComponents ?? []).some(
      (e: any) => (e?.metadata?.name ?? '') === displayName,
    );
    if (foundComponent) return false;
    return false;
  }, [stateKind, stateType, apiList, allComponents, displayName]);

  const currentEntity = useMemo(() => {
    const nameToFind = displayName;
    const apiEntity = (apiList ?? []).find(
      (e: any) => (e?.metadata?.name ?? '') === nameToFind,
    );
    const componentEntity = (allComponents ?? []).find(
      (e: any) => (e?.metadata?.name ?? '') === nameToFind,
    );
    return (
      (isApi ? apiEntity : componentEntity) ?? apiEntity ?? componentEntity
    );
  }, [isApi, apiList, allComponents, displayName]);

  const aboutEntity = currentEntity ?? item;
  const aboutName =
    (aboutEntity?.metadata?.name as string | undefined) ?? displayName;
  const aboutDescription =
    (aboutEntity?.metadata?.description as string | undefined) ?? '-';
  const aboutOwner = (aboutEntity?.spec?.owner as string | undefined) ?? '-';
  const aboutSystem = (aboutEntity?.spec?.system as string | undefined) ?? '-';
  const aboutType = (aboutEntity?.spec?.type as string | undefined) ?? '-';
  const [aboutLifecycle, setAboutLifecycle] = useState(
    (aboutEntity?.spec?.lifecycle as string | undefined) ?? '-',
  );
  const isLifecycleReady =
    (aboutLifecycle ?? '').trim().toLowerCase() === 'ready to use';
  const aboutDomain = (aboutEntity?.spec?.domain as string | undefined) ?? '-';

  const links: Array<{ label: string; url: string }> = buildLinks(
    currentEntity ?? item,
  );

  useEffect(() => {
    const nextLifecycle =
      (aboutEntity?.spec?.lifecycle as string | undefined) ?? '-';
    setAboutLifecycle(nextLifecycle);
    setSelectedLifecycle(nextLifecycle);
  }, [aboutEntity]);

  const subComponentColumns = buildSubComponentColumns();

  const [isUpdatingLifecycle, setIsUpdatingLifecycle] = useState(false);
  const [selectedLifecycle, setSelectedLifecycle] = useState(
    (aboutEntity?.spec?.lifecycle as string | undefined) ?? '-',
  );

  const deriveFileInfo = useCallback(() => {
    const annotations = (aboutEntity?.metadata?.annotations ?? {}) as Record<
      string,
      string
    >;
    const viewUrl =
      annotations['backstage.io/managed-by-location'] ||
      annotations['backstage.io/managed-by-origin-location'] ||
      annotations['backstage.io/view-url'];

    if (typeof viewUrl === 'string' && viewUrl.includes('/')) {
      const last = viewUrl.split('/').pop()?.split('?')[0] ?? '';
      if (last) return { fileName: last, fileUrl: viewUrl };
    }

    const fallback = `catalog-${aboutName}.yaml`;
    return { fileName: fallback, fileUrl: viewUrl };
  }, [aboutEntity, aboutName]);

  const parseTagsArray = useCallback((): string[] => {
    const tags = aboutEntity?.metadata?.tags as any;
    if (Array.isArray(tags)) return tags.filter(Boolean).map(String);
    if (typeof tags === 'string')
      return tags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);
    return [];
  }, [aboutEntity]);

  const aboutTagsArray = useMemo(() => parseTagsArray(), [parseTagsArray]);

  const buildEditPayload = useCallback(() => {
    const spec = aboutEntity?.spec ?? {};
    const metadata = aboutEntity?.metadata ?? {};
    const { fileName } = deriveFileInfo();
    const base = {
      fileName,
      description: (metadata as any)?.description ?? '',
      owner: (spec as any)?.owner ?? '',
      lifecycle: 'Ready to Use',
      system: (spec as any)?.system ?? '',
      domain: (spec as any)?.domain ?? '',
      tags: parseTagsArray(),
      links: (metadata as any)?.links ?? [],
    } as any;

    if (isApi) {
      base.type = (spec as any)?.type ?? 'OpenAPI';
      base.apiVersion = (spec as any)?.apiVersion ?? (spec as any)?.version;
    } else {
      base.type = (spec as any)?.type ?? '';
      base.consumesApis = (spec as any)?.consumesApis ?? [];
      base.providesApis = (spec as any)?.providesApis ?? [];
    }

    return base;
  }, [aboutEntity, deriveFileInfo, parseTagsArray, isApi]);

  const refreshEntity = useCallback(async () => {
    const kind = isApi ? 'api' : 'component';
    const namespace = (aboutEntity?.metadata?.namespace as string) ?? 'default';
    const nameValue = (aboutEntity?.metadata?.name as string) ?? aboutName;
    const entityRef = `${kind}:${namespace}/${nameValue}`;

    try {
      const { token } = await identityApi.getCredentials();
      const backendBaseUrl = configApi.getString('backend.baseUrl');
      await fetch(`${backendBaseUrl}/api/catalog/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ entityRef }),
      });
    } catch (e) {
      console.warn('Catalog refresh failed', e);
    }
  }, [aboutEntity, aboutName, configApi, identityApi, isApi]);

  const handleLifecycleUpdate = useCallback(
    async (nextLifecycleValue?: string) => {
      if (!isAdmin) {
        toast.errorMsg('Only Admin can edit lifecycle.');
        return;
      }

      const { fileName } = deriveFileInfo();
      if (!fileName) {
        toast.errorMsg('Unable to resolve file name for this entity.');
        return;
      }

      const lifecycleValue =
        nextLifecycleValue || selectedLifecycle || 'Ready to Use';

      const payload = {
        ...buildEditPayload(),
        lifecycle: lifecycleValue,
      };

      setIsUpdatingLifecycle(true);
      try {
        const { token } = await identityApi.getCredentials();
        const backendBaseUrl = configApi.getString('backend.baseUrl');
        const catalogPortalBase = `${backendBaseUrl}/api/catalog-portal`;

        const res = await fetch(`${catalogPortalBase}/register`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const msg = await (async () => {
            try {
              const data = await res.json();
              return (
                (data as any)?.error?.message ||
                (data as any)?.message ||
                (data as any)?.error ||
                ''
              );
            } catch (err) {
              return await res.text();
            }
          })();
          throw new Error(msg || 'Lifecycle update failed');
        }

        const j = await res.json().catch(() => ({} as any));
        const backupFileName = j?.backup?.fileName as string | undefined;

        if (backupFileName) {
          try {
            await fetch(`${catalogPortalBase}/blob-backup-delete`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              credentials: 'include',
              body: JSON.stringify({ backupFileName }),
            });
          } catch (err) {
            console.warn('Backup delete failed', err);
          }
        }

        const nextLifecycle = lifecycleValue || 'Ready to Use';
        setAboutLifecycle(nextLifecycle);
        toast.successMsg(`Lifecycle updated to ${nextLifecycle}.`);
        await refreshEntity();
      } catch (e: any) {
        toast.errorMsg(e?.message || 'Lifecycle update failed');
      } finally {
        setIsUpdatingLifecycle(false);
      }
    },
    [
      isAdmin,
      deriveFileInfo,
      buildEditPayload,
      identityApi,
      configApi,
      toast,
      refreshEntity,
      selectedLifecycle,
    ],
  );

  const handleLifecycleSelect = useCallback(
    (opt: any) => {
      const next = (opt?.value as string) ?? 'Ready to Use';
      setSelectedLifecycle(next);
      handleLifecycleUpdate(next);
    },
    [handleLifecycleUpdate],
  );

  const truncateText = (text: string, maxChars: number) => {
    if (!text) return text;
    if (text.length <= maxChars) return text;
    const sliceLen = Math.max(0, maxChars - 3);
    return `${text.slice(0, sliceLen)}...`;
  };

  const displayUrl = (url: string) => {
    const normalized = (url ?? '').replace(/^https?:\/\//i, '');
    return truncateText(normalized, 44);
  };

  const expandComponentRef = (raw: unknown, preferredNamespace: string) => {
    if (typeof raw !== 'string') return [] as string[];
    const s = raw.trim();
    if (!s) return [] as string[];

    const [maybeKind, maybeRest] = s.includes(':')
      ? (s.split(':', 2) as [string, string])
      : (['component', s] as [string, string]);

    const kind = (maybeKind ?? 'component').toLowerCase();
    if (kind !== 'component') return [] as string[];

    const rest = (maybeRest ?? '').replace(/^\/+/, '');
    const parts = rest.split('/').filter(Boolean);
    const ns = (preferredNamespace || 'default').toLowerCase();

    if (parts.length >= 2) {
      return [`component:${parts[0].toLowerCase()}/${parts[1].toLowerCase()}`];
    }

    const name = (parts[0] ?? '').toLowerCase();
    if (!name) return [] as string[];
    const candidates = [`component:${ns}/${name}`, `component:default/${name}`];
    return Array.from(new Set(candidates));
  };

  const relatedComponents = useMemo(() => {
    if (isApi) return [] as any[];
    const items = allComponents ?? [];
    const currentName = currentEntity?.metadata?.name ?? displayName;
    const currentNamespace = currentEntity?.metadata?.namespace ?? 'default';
    const currentRefLower = `component:${String(
      currentNamespace,
    ).toLowerCase()}/${String(currentName).toLowerCase()}`;
    return items.filter((e: any) => {
      const spec = e?.spec ?? {};
      const dependsOn: string[] = spec.dependsOn ?? [];
      const subcomponentOf: string | undefined = spec.subcomponentOf;
      const relations: any[] = (e?.relations ?? []) as any[];

      const matchesCurrent = (ref: unknown) =>
        expandComponentRef(ref, String(currentNamespace)).some(
          r => r === currentRefLower,
        );

      const partOfMatches = Array.isArray(relations)
        ? relations.some(r => {
            if ((r?.type ?? '').toLowerCase() !== 'partof') return false;
            const targetRef = (r?.targetRef ?? r?.target ?? '') as string;
            return matchesCurrent(targetRef);
          })
        : false;
      return (
        (Array.isArray(dependsOn) && dependsOn.some(d => matchesCurrent(d))) ||
        (typeof subcomponentOf === 'string' &&
          matchesCurrent(subcomponentOf)) ||
        partOfMatches
      );
    });
  }, [isApi, allComponents, displayName, currentEntity]);

  const parentComponentEntity = useMemo(() => {
    if (isApi) return undefined;
    const items = allComponents ?? [];

    const preferredNamespace = String(
      currentEntity?.metadata?.namespace ?? 'default',
    );
    const rawParentRef = (currentEntity?.spec?.subcomponentOf ?? '') as unknown;
    const candidateRefs = expandComponentRef(rawParentRef, preferredNamespace);
    if (!candidateRefs.length) return undefined;

    const candidateSet = new Set(candidateRefs.map(r => r.toLowerCase()));
    return items.find((e: any) => {
      const ns = String(e?.metadata?.namespace ?? 'default').toLowerCase();
      const nm = String(e?.metadata?.name ?? '').toLowerCase();
      return candidateSet.has(`component:${ns}/${nm}`);
    });
  }, [isApi, allComponents, currentEntity]);

  const parentComponentName = useMemo(() => {
    if (!parentComponentEntity) return '-';
    return (parentComponentEntity?.metadata?.name as string | undefined) ?? '-';
  }, [parentComponentEntity]);

  const currentApiRef = useMemo(
    () => `api:default/${displayName}`,
    [displayName],
  );
  const currentApiRefLower = useMemo(
    () => currentApiRef.toLowerCase(),
    [currentApiRef],
  );
  const providerComponents = useMemo(() => {
    if (!isApi) return [] as any[];
    const items = allComponents ?? [];
    return items.filter((e: any) => {
      const providesApis: string[] = (e?.spec?.providesApis ?? []) as string[];
      return Array.isArray(providesApis)
        ? providesApis.some(
            r =>
              (typeof r === 'string' ? r.toLowerCase() : '') ===
              currentApiRefLower,
          )
        : false;
    });
  }, [isApi, allComponents, currentApiRefLower]);

  const consumerComponents = useMemo(() => {
    if (!isApi) return [] as any[];
    const items = allComponents ?? [];
    return items.filter((e: any) => {
      const consumesApis: string[] = (e?.spec?.consumesApis ?? []) as string[];
      return Array.isArray(consumesApis)
        ? consumesApis.some(
            r =>
              (typeof r === 'string' ? r.toLowerCase() : '') ===
              currentApiRefLower,
          )
        : false;
    });
  }, [isApi, allComponents, currentApiRefLower]);

  const relationDerived = useMemo(() => {
    if (!isApi) return { provRefs: [] as string[], consRefs: [] as string[] };
    const current = (apiList ?? []).find(
      (e: any) => (e?.metadata?.name ?? '') === displayName,
    );
    const relations: any[] = (current?.relations ?? []) as any[];
    const provRefs = relations
      .filter(r => (r?.type ?? '').toLowerCase() === 'apiprovidedby')
      .map(r => (r?.targetRef as string) ?? (r?.target as string))
      .filter((r): r is string => typeof r === 'string' && r.length > 0)
      .map(r => r.toLowerCase());
    const consRefs = relations
      .filter(r => (r?.type ?? '').toLowerCase() === 'apiconsumedby')
      .map(r => (r?.targetRef as string) ?? (r?.target as string))
      .filter((r): r is string => typeof r === 'string' && r.length > 0)
      .map(r => r.toLowerCase());
    return { provRefs, consRefs };
  }, [apiList, isApi, displayName]);

  const refToEntityRef = (e: any) =>
    `${(e?.kind ?? 'Component').toLowerCase()}:${
      e?.metadata?.namespace ?? 'default'
    }/${e?.metadata?.name}`;

  const dedupeEntitiesByRef = (entities: any[]) => {
    const seen = new Set<string>();
    return (entities ?? []).filter((e: any) => {
      const ref = refToEntityRef(e).toLowerCase();
      if (seen.has(ref)) return false;
      seen.add(ref);
      return true;
    });
  };

  const providerFromRelations = useMemo(() => {
    const items = allComponents ?? [];
    const set = new Set(
      (relationDerived.provRefs ?? []).map(r => r.toLowerCase()),
    );
    return items.filter((e: any) => set.has(refToEntityRef(e).toLowerCase()));
  }, [relationDerived.provRefs, allComponents]);

  const consumerFromRelations = useMemo(() => {
    const items = allComponents ?? [];
    const set = new Set(
      (relationDerived.consRefs ?? []).map(r => r.toLowerCase()),
    );
    return items.filter((e: any) => set.has(refToEntityRef(e).toLowerCase()));
  }, [relationDerived.consRefs, allComponents]);

  const apiSubRows = useMemo(
    () => buildApiSubRows(relatedComponents ?? []),
    [relatedComponents],
  );

  const providerRows = useMemo(() => {
    const entities = dedupeEntitiesByRef([
      ...(providerComponents ?? []),
      ...providerFromRelations,
    ]);
    return buildApiSubRows(entities);
  }, [providerComponents, providerFromRelations]);

  const consumerRows = useMemo(() => {
    const entities = dedupeEntitiesByRef([
      ...(consumerComponents ?? []),
      ...consumerFromRelations,
    ]);
    return buildApiSubRows(entities);
  }, [consumerComponents, consumerFromRelations]);

  const componentApiRefs = useMemo(() => {
    if (isApi) return { provides: [] as string[], consumes: [] as string[] };
    const provides: string[] = (currentEntity?.spec?.providesApis ??
      []) as string[];
    const consumes: string[] = (currentEntity?.spec?.consumesApis ??
      []) as string[];
    return { provides, consumes };
  }, [isApi, currentEntity]);

  const { value: allApis } = useAsync(async () => {
    if (isApi) return [] as any[];
    return await getAllEntities(catalogApi, { filter: [{ kind: 'API' }] });
  }, [catalogApi, isApi]);

  const apiRefOf = (e: any) =>
    `api:${e?.metadata?.namespace ?? 'default'}/${e?.metadata?.name}`;
  const apisProvided = useMemo(() => {
    if (isApi) return [] as any[];
    const set = new Set(componentApiRefs.provides);
    return (allApis ?? []).filter((e: any) => set.has(apiRefOf(e)));
  }, [isApi, componentApiRefs.provides, allApis]);
  const apisConsumed = useMemo(() => {
    if (isApi) return [] as any[];
    const set = new Set(componentApiRefs.consumes);
    return (allApis ?? []).filter((e: any) => set.has(apiRefOf(e)));
  }, [isApi, componentApiRefs.consumes, allApis]);

  const providedRows = useMemo(
    () => buildApiSubRows(apisProvided),
    [apisProvided],
  );
  const consumedRows = useMemo(
    () => buildApiSubRows(apisConsumed),
    [apisConsumed],
  );

  const componentSections = useMemo(() => {
    if (isApi)
      return [] as Array<{
        title: string;
        rows: any[];
        columns: any[];
        showLoading?: boolean;
      }>;
    return [
      {
        title: 'APIs Consumed',
        rows: consumedRows,
        columns: subComponentColumns,
      },
      {
        title: 'APIs Provided',
        rows: providedRows,
        columns: subComponentColumns,
      },
    ];
  }, [isApi, providedRows, consumedRows, subComponentColumns]);

  const apiSections = useMemo(() => {
    if (!isApi)
      return [] as Array<{ title: string; rows: any[]; columns: any[] }>;
    return [
      { title: 'Consumers', rows: consumerRows, columns: subComponentColumns },
      { title: 'Providers', rows: providerRows, columns: subComponentColumns },
    ];
  }, [isApi, providerRows, consumerRows, subComponentColumns]);

  const componentTabs = useMemo(
    () => componentSections.map(sec => ({ label: sec.title })),
    [componentSections],
  );
  const [activeComponentTab, setActiveComponentTab] = useState<string>('');

  const apiTabs = useMemo(
    () => apiSections.map(sec => ({ label: sec.title })),
    [apiSections],
  );
  const [activeApiTab, setActiveApiTab] = useState<string>('');

  useEffect(() => {
    if (isApi) return;
    if (!componentSections.length) return;

    const titles = componentSections.map(s => s.title);
    if (!activeComponentTab || !titles.includes(activeComponentTab)) {
      setActiveComponentTab(componentSections[0].title);
    }
  }, [isApi, componentSections, activeComponentTab]);

  useEffect(() => {
    if (!isApi) return;
    if (!apiSections.length) return;

    const titles = apiSections.map(s => s.title);
    if (!activeApiTab || !titles.includes(activeApiTab)) {
      setActiveApiTab(apiSections[0].title);
    }
  }, [isApi, apiSections, activeApiTab]);

  const activeComponentSection = useMemo(() => {
    const found = componentSections.find(s => s.title === activeComponentTab);
    return found ?? componentSections[0];
  }, [componentSections, activeComponentTab]);

  const activeApiSection = useMemo(() => {
    const found = apiSections.find(s => s.title === activeApiTab);
    return found ?? apiSections[0];
  }, [apiSections, activeApiTab]);

  const handleComponentTabChange = (next: any) => {
    if (typeof next === 'string') {
      setActiveComponentTab(next);
    } else if (typeof next === 'number') {
      setActiveComponentTab(componentTabs[next]?.label ?? '');
    } else if (next?.label) {
      setActiveComponentTab(next.label);
    }
  };

  const handleApiTabChange = (next: any) => {
    if (typeof next === 'string') {
      setActiveApiTab(next);
    } else if (typeof next === 'number') {
      setActiveApiTab(apiTabs[next]?.label ?? '');
    } else if (next?.label) {
      setActiveApiTab(next.label);
    }
  };

  return {
    isApi,
    isAdmin,
    aboutName,
    aboutDescription,
    aboutOwner,
    aboutSystem,
    aboutType,
    aboutLifecycle,
    aboutDomain,
    aboutTagsArray,
    isLifecycleReady,
    readyLifecycleOptions,
    selectedLifecycle,
    handleLifecycleSelect,
    handleLifecycleUpdate,
    links,
    displayUrl,
    subComponentColumns,
    subComponentRows: apiSubRows,
    componentsLoading,
    componentsError,
    activeComponentTab,
    componentTabs,
    activeComponentSection,
    handleComponentTabChange,
    activeApiTab,
    apiTabs,
    activeApiSection,
    handleApiTabChange,
    currentEntity,
    isUpdatingLifecycle,
    parentComponentName,
  };
};
